import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

const Shop = () => {
  const { category } = useParams(); // "medicines" or "equipment"
  const cat = category === "equipment" ? "equipment" : "medicine";
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [subFilter, setSubFilter] = useState("all");

  useEffect(() => {
    api.get(`/products?category=${cat}`).then((r) => setProducts(r.data)).catch(() => {});
    setSubFilter("all");
    setSearch("");
  }, [cat]);

  const subCategories = useMemo(
    () => ["all", ...Array.from(new Set(products.map((p) => p.sub_category).filter(Boolean)))],
    [products]
  );

  const filtered = useMemo(
    () =>
      products.filter(
        (p) =>
          (subFilter === "all" || p.sub_category === subFilter) &&
          p.name.toLowerCase().includes(search.toLowerCase())
      ),
    [products, search, subFilter]
  );

  const title = cat === "medicine" ? "The Apothecary" : "Clinical Equipment";
  const subtitle =
    cat === "medicine"
      ? "Hand-ground formulations, prepared in small batches, sourced from organic farms."
      : "Authentic, hand-crafted equipment for the practising Ayurvedic clinic — built to last generations.";

  return (
    <div data-testid={`shop-${cat}-page`}>
      <section className="px-6 md:px-12 lg:px-20 pt-24 pb-12 md:pt-32">
        <p className="overline mb-6">{cat === "medicine" ? "Herbal & Ayurvedic Medicines" : "Panchakarma Equipment"}</p>
        <h1 className="text-5xl sm:text-7xl tracking-tight font-light max-w-4xl leading-[1.05] mb-6">{title}</h1>
        <p className="max-w-2xl leading-relaxed" style={{ color: "var(--ayu-body)" }}>{subtitle}</p>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b pb-6" style={{ borderColor: "var(--ayu-border)" }}>
          <div className="flex flex-wrap gap-2">
            {subCategories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c}`}
                onClick={() => setSubFilter(c)}
                className={`text-sm px-4 py-2 transition-colors ${
                  subFilter === c ? "bg-[var(--ayu-primary)] text-[var(--ayu-bg)]" : "border border-[var(--ayu-border)] hover:border-[var(--ayu-primary)]"
                }`}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border-b border-[var(--ayu-border)] py-1 min-w-[260px]">
            <Search size={16} strokeWidth={1.5} style={{ color: "var(--ayu-muted)" }} />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${cat === "medicine" ? "medicines" : "equipment"}…`}
              className="bg-transparent flex-1 py-2 outline-none text-sm"
            />
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 pb-24">
        {filtered.length === 0 ? (
          <p data-testid="empty-state" className="py-20 text-center" style={{ color: "var(--ayu-muted)" }}>
            No items match your search.
          </p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="group block bg-[var(--ayu-surface)] p-3 hover:bg-[var(--ayu-accent)]/40 transition-colors"
              >
                <div className="aspect-square overflow-hidden mb-4">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="px-2 pb-3">
                  <p className="text-xs opacity-60 mb-1">{p.sub_category}</p>
                  <h4 className="font-display text-xl leading-snug">{p.name}</h4>
                  <p className="text-sm mt-2 opacity-70 line-clamp-2">{p.short_description}</p>
                  <p className="mt-4 text-base flex items-center justify-between" style={{ color: "var(--ayu-secondary)" }}>
                    <span>₹{p.price.toLocaleString("en-IN")}</span>
                    {!p.in_stock && (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-sm border border-[#FAEAF0] bg-[#FAEAF0] text-[#A8324A]">
                        Out of Stock
                      </span>
                    )}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Shop;
